import { faFilter, faParagraph, faRobot } from '@fortawesome/pro-regular-svg-icons';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { chain } from 'lodash';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import ApproveAllContentPlanPopover from '../../../../../components/muiLegacy/Popover/ApproveAllContentPlanPopover';
import VisibleColumnsContentPlanPopover from '../../../../../components/muiLegacy/Popover/VisibleColumnsContentPlanPopover';
import VoxelButton from '../../../../../components/voxel/Buttons/VoxelButton';
import VoxelPanel from '../../../../../components/voxel/Surfaces/VoxelPanel';
import HeadCellBoolean from '../../../../../components/muiLegacy/Tables/Shared/HeadCellBoolean';
import HeadCellNumber from '../../../../../components/muiLegacy/Tables/Shared/HeadCellNumber';
import HeadCellTagGroup from '../../../../../components/muiLegacy/Tables/Shared/HeadCellTagGroup';
import HeadCellText from '../../../../../components/muiLegacy/Tables/Shared/HeadCellText';
import { FastTagGroup } from '../../../../../components/muiLegacy/Tables/Shared/TableCellTagGroups';
import VoxelAdvancedTextSearch from '../../../../../components/voxel/Search/VoxelAdvancedTextSearch';
import { ContentTaskFilteringGq } from '../../../../../graphql/projects/models/ContentTaskFilteringGq';
import { ContentTaskGq } from '../../../../../graphql/projects/models/ContentTaskGq';
import { ContentTaskKeywordFilterGq } from '../../../../../graphql/projects/models/ContentTaskKeywordFilterGq';
import { ContentTaskSortingTypeGq } from '../../../../../graphql/projects/models/ContentTaskSortingTypeGq';
import { DomainTypeGq } from '../../../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { TagGq } from '../../../../../graphql/projects/models/TagGq';
import { CommandGq } from '../../../../../graphql/projects/queries/contentPlan/useGetContentTasksGq';
import { DisplaySerpResult } from '../../../../DataManagementPage/components/Dialogs/SerpResultsDialog';
import ContentPlanDownloadButton from '../../Buttons/ContentTaskTable/ContentPlanDownloadButton';
import ContentTaskTableRow from './ContentTaskTableRow';
import HeadCellAction from './HeadCellAction';
import HeadCellApproved from './HeadCellApproved';
import HeadCellPrimaryIcon from './HeadCellPrimaryIcon';
import HeadCellSecondaryIcon from './HeadCellSecondaryIcon';
import HeadCellStatusCode from './HeadCellStatusCode';
import { useVisibleColumns } from './useVisibleColumns';
import ApplyChatGptDialog from '../../Dialogs/ApplyChatGptDialog/ApplyChatGptDialog';
import HeadCell from '../../../../../components/muiLegacy/Tables/Shared/HeadCell';
import { usersApiService } from '../../../../../graphql/users/usersApiService';
import HeadCellBasic from '../../../../../components/muiLegacy/Tables/Shared/HeadCellBasic';

export interface ContentTaskTableProps {
  project: ProjectGq;
  hasTagPlan?: boolean;
  disabled: boolean;
  contentTasks: ContentTaskGq[];
  count: number;
  page: number;
  rowsPerPage: number;
  reload: () => Promise<any>;
  initialFilteringState: CommandGq;
  filteringState: CommandGq;
  setFilteringState: Dispatch<SetStateAction<CommandGq>>;
  setDisplaySerpResult: (state: DisplaySerpResult) => void;
  setSelectedContentTask: (contentTask: ContentTaskGq) => void;
  selectedContentTaskId?: number;
  filterNotChanged: boolean;
  setAddMissingOpen: Dispatch<SetStateAction<boolean>>;
  showAddMissing: boolean;
  openMetaRules: () => void;
}

interface KeywordRange {
  orderMin: number | undefined;
  orderMax: number | undefined;
}
export const ranges = {
  primary: { orderMin: 1, orderMax: 1 } as KeywordRange,
  secondary: { orderMin: 2, orderMax: 2 } as KeywordRange,
  longTail: { orderMin: 3, orderMax: undefined } as KeywordRange,
  all: { orderMin: undefined, orderMax: undefined } as KeywordRange,
};

export const ContentTaskTable: React.FC<ContentTaskTableProps> = ({
  project,
  disabled,
  hasTagPlan,
  contentTasks,
  reload,
  initialFilteringState,
  filteringState,
  setFilteringState,
  setDisplaySerpResult,
  selectedContentTaskId,
  setSelectedContentTask,
  count,
  page,
  rowsPerPage,
  filterNotChanged,
  openMetaRules,
}) => {
  const userQuery = usersApiService.queries.useGetCurrentUser(undefined, {
    fetchPolicy: 'cache-first',
  });

  // Filter logic
  const handleFilter = (filter: Partial<ContentTaskFilteringGq>) =>
    setFilteringState((prev) => ({ ...prev, filtering: { ...prev.filtering, ...filter } }));

  const handleFilterKeyword = (
    filter: Partial<ContentTaskKeywordFilterGq>,
    keywordRange: KeywordRange
  ) =>
    setFilteringState((prev) => ({
      ...prev,
      filtering: {
        ...prev.filtering,
        keywordFilters: prev.filtering.keywordFilters.map((keywordFilter) => {
          if (
            keywordFilter.orderMin === keywordRange.orderMin &&
            keywordFilter.orderMax == keywordRange.orderMax
          ) {
            return { ...keywordFilter, ...filter };
          }
          return keywordFilter;
        }),
      },
    }));

  const getKeywordFilter = (range: KeywordRange) =>
    filteringState.filtering.keywordFilters.find(
      (kwf) => kwf.orderMin === range.orderMin && kwf.orderMax === range.orderMax
    );
  const keywordFilters = useMemo(
    () => ({
      primary: getKeywordFilter(ranges.primary),
      secondary: getKeywordFilter(ranges.secondary),
      longTail: getKeywordFilter(ranges.longTail),
      all: getKeywordFilter(ranges.all),
    }),
    [filteringState]
  );

  // Sort logic
  const handleSort = (
    type: ContentTaskSortingTypeGq,
    direction: 'asc' | 'desc',
    keywordOrder?: number,
    sortId?: number
  ) =>
    setFilteringState((prev) => {
      const next = { ...prev };
      if (
        prev.sorting?.type !== type ||
        prev.sorting?.direction !== direction ||
        prev.sorting?.keywordOrder !== keywordOrder ||
        prev.sorting?.sortId !== sortId
      ) {
        next.sorting = {
          type,
          direction,
          keywordOrder,
          sortId,
        };
      } else {
        next.sorting = undefined;
      }
      return next;
    });

  const getSortDirection = (
    sortDirection: ContentTaskSortingTypeGq,
    keywordOrder?: number,
    sortId?: number
  ) =>
    filteringState.sorting?.type === sortDirection &&
      filteringState.sorting?.keywordOrder === keywordOrder &&
      filteringState.sorting?.sortId === sortId
      ? filteringState.sorting?.direction
      : undefined;

  // Reset page if relevant
  useEffect(
    () => setFilteringState((prev) => ({ ...prev, page: 0 })),
    [filteringState.rowsPerPage, filteringState.filtering, filteringState.sorting]
  );

  const projectDomain = project.domains.find((d) => d.type === DomainTypeGq.PROJECT);
  const clientUrl = projectDomain?.protocol + projectDomain?.hostName;

  const clientHostName =
    project.domains.find((d) => d.type === DomainTypeGq.PROJECT)?.hostName ?? '';

  const cleanedClientHostName = clientHostName?.endsWith('/')
    ? clientHostName.slice(0, clientHostName.length - 1)
    : clientHostName;

  // Visible columns
  const tagGroups = useMemo(
    () =>
      hasTagPlan
        ? chain(project.tagGroups)
          .filter((tg) => tg.inContentPlan)
          .orderBy((tg) => tg.contentPlanOrder)
          .value()
        : [],
    [hasTagPlan, project.tagGroups]
  );

  const visibleColumnsBag = useVisibleColumns(project);
  const shownTagGroups = useMemo(
    () => tagGroups.filter((tg) => visibleColumnsBag.state.shownTagGroupIds.includes(tg.id)),
    [tagGroups, visibleColumnsBag.state.shownTagGroupIds]
  );

  // Fast tag groups
  const fastTagGroups = useMemo(
    () =>
      shownTagGroups.map((tagGroup): FastTagGroup => {
        const tags: Record<number, TagGq> = {};
        tagGroup.tags.forEach((tag) => (tags[tag.id] = tag));
        return {
          tagGroup,
          tags,
        };
      }),
    [shownTagGroups]
  );

  // Chat GPT dialog
  const [showChatGptDialog, setShowChatGptDialog] = useState(false);

  return (
    <VoxelPanel>
      <div className="px-3 py-2 flex flex-row items-start justify-between">
        <div className="flex flex-row items-center gap-2">
          <VoxelAdvancedTextSearch
            widthClass="w-56"
            size="sm"
            value={keywordFilters.all.searchTerm}
            onChange={(event) =>
              handleFilterKeyword({ searchTerm: event.target.value }, ranges.all)
            }
            placeholder={'Keyword filter'}
            deleteValue={() => handleFilterKeyword({ searchTerm: '' }, ranges.all)}
          />
          {!filterNotChanged && (
            <VoxelButton
              title={'Active filters'}
              startIcon={<FontAwesomeIcon icon={faFilter} />}
              endIcon={<FontAwesomeIcon icon={faXmark} size={'lg'} />}
              size="md"
              onClick={() => setFilteringState(initialFilteringState)}
            />
          )}
        </div>
        <div className={'flex flex-row items-center gap-3'}>
          {!disabled && (
            <VoxelButton
              tooltip="Generate content with ChatGPT"
              size="md"
              title="ChatGPT"
              color="light-gray"
              endIcon={
                <>
                  <FontAwesomeIcon icon={faRobot} />
                  <span className="uppercase rounded-full ring-1 ring-gray-400 px-1.5 text-xs ml-2 text-gray-400">
                    beta
                  </span>
                </>
              }
              onClick={() => setShowChatGptDialog(true)}
              tooltipProps={{ placement: 'top' }}
            />
          )}
          <ApplyChatGptDialog
            open={showChatGptDialog}
            handleClose={() => setShowChatGptDialog(false)}
            project={project}
            filteringState={filteringState.filtering}
            sortingState={filteringState.sorting}
            reload={reload}
          />
          <VoxelButton
            title={'Generate Meta rules'}
            size={'md'}
            color="light-gray"
            onClick={openMetaRules}
            disabled={disabled}
            endIcon={<FontAwesomeIcon icon={faParagraph} />}
          />
          {!disabled && (
            <ApproveAllContentPlanPopover
              filteringState={filteringState.filtering}
              reload={reload}
            />
          )}
          <ContentPlanDownloadButton
            projectId={project.id}
            filter={filteringState.filtering}
            disabled={disabled}
          />
          <VisibleColumnsContentPlanPopover
            visibleColumnsBag={visibleColumnsBag}
            tagGroups={tagGroups}
          />
        </div>
      </div>
      <TableContainer className={'max-h-[calc(100vh-224px)]'}>
        <Table size={'small'} stickyHeader>
          <TableHead>
            <TableRow>
              <HeadCellText
                basicConfig={{
                  title: ' ' + cleanedClientHostName + '...',
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.url,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.URL),
                  toggleSortDirection: (state) => handleSort(ContentTaskSortingTypeGq.URL, state),
                  hideColumn: () => visibleColumnsBag.setState((prev) => ({ ...prev, url: false })),
                }}
                filterState={filteringState.filtering.urlSearchTerm}
                setFilterState={(value) => handleFilter({ urlSearchTerm: value })}
              />
              {visibleColumnsBag.state.approved && (
                <HeadCellApproved
                  basicConfig={{
                    title: 'Status',
                    showSorting: true,
                  }}
                  basicState={{
                    showColumn: visibleColumnsBag.state.approved,
                    sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.APPROVED_DATE),
                    toggleSortDirection: (state) =>
                      handleSort(ContentTaskSortingTypeGq.APPROVED_DATE, state),
                    hideColumn: () =>
                      visibleColumnsBag.setState((prev) => ({ ...prev, approved: false })),
                  }}
                  command={filteringState}
                  setCommand={setFilteringState}
                  minState={filteringState.filtering.approvedStatusDateTimeMin}
                  maxState={filteringState.filtering.approvedStatusDateTimeMax}
                  setMinState={(value) => handleFilter({ approvedStatusDateTimeMin: value })}
                  setMaxState={(value) => handleFilter({ approvedStatusDateTimeMax: value })}
                />
              )}
              {visibleColumnsBag.state.action && (
                <HeadCellAction
                  projectId={project.id}
                  command={filteringState}
                  setCommand={setFilteringState}
                  basicConfig={{
                    title: 'Action',
                    showSorting: true,
                  }}
                  basicState={{
                    showColumn: visibleColumnsBag.state.action,
                    sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.ACTION),
                    toggleSortDirection: (state) =>
                      handleSort(ContentTaskSortingTypeGq.ACTION, state),
                    hideColumn: () =>
                      visibleColumnsBag.setState((prev) => ({ ...prev, action: false })),
                  }}
                />
              )}
              {shownTagGroups.map((tagGroup) => (
                <HeadCellTagGroup
                  tagGroup={tagGroup}
                  key={tagGroup.id}
                  basicConfig={{
                    title: tagGroup.name,
                    tooltip: 'Tag group',
                    showSorting: true,
                  }}
                  basicState={{
                    showColumn: visibleColumnsBag.state.shownTagGroupIds.some(
                      (t) => t === tagGroup.id
                    ),
                    sortDirectionState: getSortDirection(
                      ContentTaskSortingTypeGq.TAG_GROUP,
                      undefined,
                      tagGroup.id
                    ),
                    toggleSortDirection: (state) =>
                      handleSort(ContentTaskSortingTypeGq.TAG_GROUP, state, undefined, tagGroup.id),
                    hideColumn: () =>
                      visibleColumnsBag.setState((prev) => ({
                        ...prev,
                        shownTagGroupIds: prev.shownTagGroupIds.filter((t) => t != tagGroup.id),
                      })),
                  }}
                  state={filteringState.filtering.tagFilters}
                  setState={(tagFilters) => handleFilter({ tagFilters })}
                />
              ))}

              {visibleColumnsBag.state.statusCode && (
                <HeadCellStatusCode
                  basicConfig={{
                    title: 'HTTP',
                    showSorting: true,
                  }}
                  basicState={{
                    showColumn: visibleColumnsBag.state.statusCode,
                    sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.STATUS_CODE),
                    toggleSortDirection: (state) =>
                      handleSort(ContentTaskSortingTypeGq.STATUS_CODE, state),
                    hideColumn: () =>
                      visibleColumnsBag.setState((prev) => ({ ...prev, statusCode: false })),
                  }}
                  projectId={project.id}
                  command={filteringState}
                  setCommand={setFilteringState}
                />
              )}

              {visibleColumnsBag.state.chatGpt && (
                <HeadCellBasic
                  align="center"
                  basicConfig={{ title: 'ChatGPT', showSorting: false }}
                  basicState={{
                    showColumn: true,
                    hideColumn: () =>
                      visibleColumnsBag.setState((prev) => ({
                        ...prev,
                        chatGpt: false,
                      })),
                  }}
                />
              )}

              <HeadCellNumber
                basicConfig={{ title: 'Volume', showSorting: true }}
                basicState={{
                  showColumn: visibleColumnsBag.state.totalVolume,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.TOTAL_VOLUME),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.TOTAL_VOLUME, state),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      totalVolume: false,
                    })),
                }}
                minFilterState={filteringState.filtering.totalVolumeMin}
                maxFilterState={filteringState.filtering.totalVolumeMax}
                setMinFilterState={(value) => handleFilter({ totalVolumeMin: value })}
                setMaxFilterState={(value) => handleFilter({ totalVolumeMax: value })}
              />
              <HeadCellNumber
                basicConfig={{ title: 'Traffic', showSorting: true }}
                basicState={{
                  showColumn: visibleColumnsBag.state.totalTraffic,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.TOTAL_TRAFFIC),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.TOTAL_TRAFFIC, state),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      totalTraffic: false,
                    })),
                }}
                minFilterState={filteringState.filtering.totalTrafficMin}
                maxFilterState={filteringState.filtering.totalTrafficMax}
                setMinFilterState={(value) => handleFilter({ totalTrafficMin: value })}
                setMaxFilterState={(value) => handleFilter({ totalTrafficMax: value })}
              />
              <HeadCellText
                basicConfig={{
                  title: <HeadCellPrimaryIcon align="left">Keyword</HeadCellPrimaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.primary.keyword,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD, 1),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD, state, 1),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      primary: { ...prev.primary, keyword: false },
                    })),
                }}
                filterState={keywordFilters.primary.searchTerm}
                setFilterState={(value) =>
                  handleFilterKeyword({ searchTerm: value }, ranges.primary)
                }
              />
              {visibleColumnsBag.state.primary.englishTranslation && (
                <HeadCellText
                  basicConfig={{
                    title: <HeadCellPrimaryIcon align="left">English</HeadCellPrimaryIcon>,
                    showSorting: true,
                  }}
                  basicState={{
                    showColumn: visibleColumnsBag.state.primary.englishTranslation,
                    sortDirectionState: getSortDirection(
                      ContentTaskSortingTypeGq.KEYWORD_ENGLISH,
                      1
                    ),
                    toggleSortDirection: (state) =>
                      handleSort(ContentTaskSortingTypeGq.KEYWORD_ENGLISH, state, 1),
                    hideColumn: () =>
                      visibleColumnsBag.setState((prev) => ({
                        ...prev,
                        primary: { ...prev.primary, englishTranslation: false },
                      })),
                  }}
                  filterState={keywordFilters.primary.englishSearchTerm}
                  setFilterState={(value) =>
                    handleFilterKeyword({ englishSearchTerm: value }, ranges.primary)
                  }
                />
              )}

              <HeadCellBoolean
                basicConfig={{
                  title: <HeadCellPrimaryIcon align="left">Title</HeadCellPrimaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.primary.inTitle,
                  sortDirectionState: getSortDirection(
                    ContentTaskSortingTypeGq.KEYWORD_IN_TITLE,
                    1
                  ),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_IN_TITLE, state, 1),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      primary: { ...prev.primary, inTitle: false },
                    })),
                }}
                state={keywordFilters.primary.inTitle}
                setState={(value) => handleFilterKeyword({ inTitle: value }, ranges.primary)}
              />

              <HeadCellBoolean
                basicConfig={{
                  title: <HeadCellPrimaryIcon align="left">H1</HeadCellPrimaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.primary.inh1,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_IN_H_1, 1),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_IN_H_1, state, 1),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      primary: { ...prev.primary, inh1: false },
                    })),
                }}
                state={keywordFilters.primary.inH1}
                setState={(value) => handleFilterKeyword({ inH1: value }, ranges.primary)}
              />

              <HeadCellBoolean
                basicConfig={{
                  title: <HeadCellPrimaryIcon align="left">Content</HeadCellPrimaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.primary.inContent,
                  sortDirectionState: getSortDirection(
                    ContentTaskSortingTypeGq.KEYWORD_IN_CONTENT,
                    1
                  ),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_IN_CONTENT, state, 1),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      primary: { ...prev.primary, inContent: false },
                    })),
                }}
                state={keywordFilters.primary.inContent}
                setState={(value) => handleFilterKeyword({ inContent: value }, ranges.primary)}
              />

              <HeadCellNumber
                basicConfig={{
                  title: <HeadCellPrimaryIcon>Rank on URL</HeadCellPrimaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.primary.rank,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_RANK, 1),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_RANK, state, 1),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      primary: { ...prev.primary, rank: false },
                    })),
                }}
                minFilterState={keywordFilters.primary.minRank}
                maxFilterState={keywordFilters.primary.maxRank}
                setMinFilterState={(value) =>
                  handleFilterKeyword({ minRank: value }, ranges.primary)
                }
                setMaxFilterState={(value) =>
                  handleFilterKeyword({ maxRank: value }, ranges.primary)
                }
              />
              <HeadCellNumber
                basicConfig={{
                  title: <HeadCellPrimaryIcon>Volume</HeadCellPrimaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.primary.volume,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_VOLUME, 1),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_VOLUME, state, 1),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      primary: { ...prev.primary, volume: false },
                    })),
                }}
                minFilterState={keywordFilters.primary.volumeMin}
                maxFilterState={keywordFilters.primary.volumeMax}
                setMinFilterState={(value) =>
                  handleFilterKeyword({ volumeMin: value }, ranges.primary)
                }
                setMaxFilterState={(value) =>
                  handleFilterKeyword({ volumeMax: value }, ranges.primary)
                }
              />
              <HeadCellNumber
                basicConfig={{
                  title: <HeadCellPrimaryIcon>Traffic</HeadCellPrimaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.primary.traffic,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_TRAFFIC, 1),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_TRAFFIC, state, 1),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      primary: { ...prev.primary, traffic: false },
                    })),
                }}
                minFilterState={keywordFilters.primary.trafficMin}
                maxFilterState={keywordFilters.primary.trafficMax}
                setMinFilterState={(value) =>
                  handleFilterKeyword({ trafficMin: value }, ranges.primary)
                }
                setMaxFilterState={(value) =>
                  handleFilterKeyword({ trafficMax: value }, ranges.primary)
                }
              />
              <HeadCellText
                basicConfig={{
                  title: <HeadCellSecondaryIcon>Keyword</HeadCellSecondaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.secondary.keyword,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD, 2),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD, state, 2),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      secondary: { ...prev.secondary, keyword: false },
                    })),
                }}
                filterState={keywordFilters.secondary.searchTerm}
                setFilterState={(value) =>
                  handleFilterKeyword({ searchTerm: value }, ranges.secondary)
                }
              />
              {visibleColumnsBag.state.secondary.englishTranslation && (
                <HeadCellText
                  basicConfig={{
                    title: <HeadCellSecondaryIcon align="left">English</HeadCellSecondaryIcon>,
                    showSorting: true,
                  }}
                  basicState={{
                    showColumn: visibleColumnsBag.state.secondary.englishTranslation,
                    sortDirectionState: getSortDirection(
                      ContentTaskSortingTypeGq.KEYWORD_ENGLISH,
                      2
                    ),
                    toggleSortDirection: (state) =>
                      handleSort(ContentTaskSortingTypeGq.KEYWORD_ENGLISH, state, 2),
                    hideColumn: () =>
                      visibleColumnsBag.setState((prev) => ({
                        ...prev,
                        secondary: { ...prev.secondary, englishTranslation: false },
                      })),
                  }}
                  filterState={keywordFilters.secondary.englishSearchTerm}
                  setFilterState={(value) =>
                    handleFilterKeyword({ englishSearchTerm: value }, ranges.secondary)
                  }
                />
              )}

              <HeadCellBoolean
                basicConfig={{
                  title: <HeadCellSecondaryIcon align="left">Title</HeadCellSecondaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.secondary.inTitle,
                  sortDirectionState: getSortDirection(
                    ContentTaskSortingTypeGq.KEYWORD_IN_TITLE,
                    2
                  ),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_IN_TITLE, state, 2),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      secondary: { ...prev.secondary, inTitle: false },
                    })),
                }}
                state={keywordFilters.secondary.inTitle}
                setState={(value) => handleFilterKeyword({ inTitle: value }, ranges.secondary)}
              />

              <HeadCellBoolean
                basicConfig={{
                  title: <HeadCellSecondaryIcon align="left">H1</HeadCellSecondaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.secondary.inh1,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_IN_H_1, 2),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_IN_H_1, state, 2),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      secondary: { ...prev.secondary, inh1: false },
                    })),
                }}
                state={keywordFilters.secondary.inH1}
                setState={(value) => handleFilterKeyword({ inH1: value }, ranges.secondary)}
              />

              <HeadCellBoolean
                basicConfig={{
                  title: <HeadCellSecondaryIcon align="left">Content</HeadCellSecondaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.secondary.inContent,
                  sortDirectionState: getSortDirection(
                    ContentTaskSortingTypeGq.KEYWORD_IN_CONTENT,
                    2
                  ),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_IN_CONTENT, state, 2),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      secondary: { ...prev.secondary, inContent: false },
                    })),
                }}
                state={keywordFilters.secondary.inContent}
                setState={(value) => handleFilterKeyword({ inContent: value }, ranges.secondary)}
              />

              <HeadCellNumber
                basicConfig={{
                  title: <HeadCellSecondaryIcon>Rank on URL</HeadCellSecondaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.secondary.rank,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_RANK, 2),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_RANK, state, 2),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      secondary: { ...prev.secondary, rank: false },
                    })),
                }}
                minFilterState={keywordFilters.secondary.minRank}
                maxFilterState={keywordFilters.secondary.maxRank}
                setMinFilterState={(value) =>
                  handleFilterKeyword({ minRank: value }, ranges.secondary)
                }
                setMaxFilterState={(value) =>
                  handleFilterKeyword({ maxRank: value }, ranges.secondary)
                }
              />
              <HeadCellNumber
                basicConfig={{
                  title: <HeadCellSecondaryIcon>Volume</HeadCellSecondaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.secondary.volume,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_VOLUME, 2),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_VOLUME, state, 2),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      secondary: { ...prev.secondary, volume: false },
                    })),
                }}
                minFilterState={keywordFilters.secondary.volumeMin}
                maxFilterState={keywordFilters.secondary.volumeMax}
                setMinFilterState={(value) =>
                  handleFilterKeyword({ volumeMin: value }, ranges.secondary)
                }
                setMaxFilterState={(value) =>
                  handleFilterKeyword({ volumeMax: value }, ranges.secondary)
                }
              />
              <HeadCellNumber
                basicConfig={{
                  title: <HeadCellSecondaryIcon>Traffic</HeadCellSecondaryIcon>,
                  showSorting: true,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.secondary.traffic,
                  sortDirectionState: getSortDirection(ContentTaskSortingTypeGq.KEYWORD_TRAFFIC, 2),
                  toggleSortDirection: (state) =>
                    handleSort(ContentTaskSortingTypeGq.KEYWORD_TRAFFIC, state, 2),
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      secondary: { ...prev.secondary, traffic: false },
                    })),
                }}
                minFilterState={keywordFilters.secondary.trafficMin}
                maxFilterState={keywordFilters.secondary.trafficMax}
                setMinFilterState={(value) =>
                  handleFilterKeyword({ trafficMin: value }, ranges.secondary)
                }
                setMaxFilterState={(value) =>
                  handleFilterKeyword({ trafficMax: value }, ranges.secondary)
                }
              />
              <HeadCellText
                basicConfig={{
                  title: 'Longtail keywords',
                  showSorting: false,
                }}
                basicState={{
                  showColumn: visibleColumnsBag.state.longTailKeywords,
                  hideColumn: () =>
                    visibleColumnsBag.setState((prev) => ({
                      ...prev,
                      longTailKeywords: false,
                    })),
                }}
                filterState={keywordFilters.longTail.searchTerm}
                setFilterState={(value) =>
                  handleFilterKeyword({ searchTerm: value }, ranges.longTail)
                }
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {contentTasks?.map((contentTask) => (
              <ContentTaskTableRow
                user={userQuery?.user}
                columnState={visibleColumnsBag.state}
                clientUrl={clientUrl}
                contentTask={contentTask}
                key={contentTask.id}
                shownTagGroups={fastTagGroups}
                setSelectedContentTask={setSelectedContentTask}
                selected={selectedContentTaskId === contentTask.id}
                reload={reload}
                disabled={disabled}
                setDisplaySerpResult={setDisplaySerpResult}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={'div'}
        count={count ?? 0}
        page={page ?? filteringState.page}
        rowsPerPage={rowsPerPage ?? filteringState.rowsPerPage}
        rowsPerPageOptions={[]}
        onPageChange={(event, page) => setFilteringState((prev) => ({ ...prev, page }))}
        onRowsPerPageChange={(event) => {
          const value = event.target.value;
          setFilteringState((prev) => ({ ...prev, rowsPerPage: +value }));
        }}
      />
    </VoxelPanel>
  );
};

export default ContentTaskTable;
